import {
  Column,
  EdgeDefinition,
  ExpandedTable,
  tableDefinitions,
  TableDefinitions,
} from "./constants";
function toLowerCaseString(string: string): string {
  return string.toLowerCase();
}

function expandEdge(
  edgeName: string,
  edgeDef: EdgeDefinition,
  definitions: TableDefinitions,
  visited: Set<string>
): Column {
  if (visited.has(edgeDef.table)) {
    // Avoid recursion/cycle
    return {
      name: toLowerCaseString(edgeName),
      type: "edge",
      infinite: true,
      columns: [],
      displayName: edgeDef?.displayName,
      table: edgeDef?.table,
    };
  }
  visited.add(edgeDef.table); // Mark the table as visited to avoid recursion
  const edgeTable: Column = {
    name: toLowerCaseString(edgeName),
    type: "edge",
    columns: [],
    displayName: edgeDef?.displayName,
    table: edgeDef?.table,
  };

  // Get related table's attributes and edges
  const relatedTableDef = definitions[edgeDef.table];

  if (relatedTableDef) {
    // Add attributes of the related table
    for (const [relatedAttrName, relatedAttrValue] of Object.entries(
      relatedTableDef.attributes
    )) {
      edgeTable.columns!.push({
        name: relatedAttrName,
        type: "attribute",
        displayName: relatedAttrValue?.displayName,
      });
    }

    // Recursively add nested edges of the related table
    for (const [nestedEdgeName, nestedEdgeDef] of Object.entries(
      relatedTableDef.edges
    )) {
      const nestedEdgeTable = expandEdge(
        nestedEdgeName,
        nestedEdgeDef,
        definitions,
        visited
      );
      edgeTable.columns!.push(nestedEdgeTable);
    }
  }

  visited.delete(edgeDef.table); // Remove from visited to allow re-entry on a different path
  return edgeTable;
}

export function expandTableDefinitions(
  definitions: TableDefinitions
): ExpandedTable[] {
  const expandedTables: ExpandedTable[] = [];

  // Iterate through each table in definitions
  for (const [tableName, tableDef] of Object.entries(definitions)) {
    const table: ExpandedTable = {
      name: toLowerCaseString(tableName),
      columns: [],
    };

    const visited = new Set<string>(); // Set to track visited tables for cycle prevention

    // Add attributes as columns
    for (const [attrName, attrValue] of Object.entries(tableDef.attributes)) {
      table.columns.push({
        name: attrName,
        type: "attribute",
        displayName: attrValue?.displayName,
      });
    }

    // Add edges as nested tables (edges with their own columns)
    for (const [edgeName, edgeDef] of Object.entries(tableDef.edges)) {
      const edgeTable = expandEdge(edgeName, edgeDef, definitions, visited);
      table.columns.push(edgeTable);
    }

    expandedTables.push(table);
  }

  return expandedTables;
}
